import { authApi } from '@shared/api-client';
import * as React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { useFormError } from '@/hooks/use-form-errors';

export const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { error, captureError, clearError } = useFormError();
  const [passwordError, setPasswordError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const email = searchParams.get('email') ?? '';

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    clearError();
    setPasswordError(null);

    const form = new FormData(event.currentTarget);
    const password = String(form.get('password'));

    if (password.length < 12) {
      setPasswordError('Use at least 12 characters');
      return;
    }

    if (password !== String(form.get('confirmPassword'))) {
      setPasswordError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    try {
      await authApi.confirmForgotPassword({
        email: String(form.get('email')),
        code: String(form.get('code')).trim(),
        password,
      });

      navigate('/signin', { replace: true });
    } catch (err) {
      captureError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Choose a new password</CardTitle>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error ? <Alert tone="error">{error}</Alert> : null}

          <Field label="Email" htmlFor="email">
            <Input
              name="email"
              type="email"
              defaultValue={email}
              readOnly={Boolean(email)}
              required
              className={email ? 'bg-surface-muted' : undefined}
            />
          </Field>

          <Field label="Reset code" htmlFor="code">
            <Input
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              autoFocus
              placeholder="123456"
            />
          </Field>

          <Field
            label="New password"
            htmlFor="password"
            hint="At least 12 characters, with upper and lower case and a number."
          >
            <Input
              name="password"
              type="password"
              autoComplete="new-password"
              required
            />
          </Field>

          <Field
            label="Confirm new password"
            htmlFor="confirmPassword"
            error={passwordError}
          >
            <Input
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              required
            />
          </Field>

          <Button type="submit" className="w-full" loading={isSubmitting}>
            Update password
          </Button>
        </form>

        <p className="mt-5 text-center text-sm">
          <Link
            to="/signin"
            className="text-ink-muted underline-offset-4 hover:text-ink hover:underline"
          >
            Back to sign in
          </Link>
        </p>
      </CardContent>
    </Card>
  );
};
